<template>
  <ui-modal
    v-model="opened"
    modal-classes="location-qr-code-preview-modal"
    :is-loading="loading.image"
    @closed="onClosed"
  >
    <div class="location-qr-code-preview-modal__body">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="codeName"
        class="location-qr-code-preview-modal__image"
      />
      <p
        v-else
        class="location-qr-code-preview-modal__invalid text-grey mb-0"
      >
        {{ $t('checkinSettings.download.displayError') }}
      </p>

      <p class="location-qr-code-preview-modal__name font-16 mb-0">
        {{ codeName }}
      </p>
    </div>

    <template #footer>
      <ui-button
        round
        block
        outline
        type="default"
        native-type="button"
        :is-loading="loading.download"
        :disabled="!imageBlob || loading.image || loading.download"
        @click="downloadSvg"
      >
        {{ $t('checkinSettings.download.downloadSvg') }}
      </ui-button>
    </template>
  </ui-modal>
</template>

<script>
import api from '@/api';
import downloadFile from '@/utils/download';

export default {
  name: 'LocationQrCodePreviewModal',

  model: {
    prop: 'show',
    event: 'input',
  },

  props: {
    show: Boolean,
    workplaceId: {
      type: [String, Number],
      required: true,
    },
    code: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    imageBlob: null,
    imageUrl: null,
    filename: null,
    loading: {
      image: false,
      download: false,
    },
  }),

  computed: {
    opened: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    codeName() {
      return this.code?.qrName || this.code?.id || '';
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value && this.code?.id) {
          this.loadQr();
        }
      },
    },
  },

  methods: {
    fallbackFilename() {
      const name = `${this.codeName || 'QRCode'}`
        .replace(/[\\/:*?"<>|]/g, '')
        .trim() || 'QRCode';

      return `${name}.svg`;
    },

    async loadQr() {
      if (!this.code?.id) {
        return;
      }

      this.revokeImage();
      this.loading.image = true;

      try {
        const { data, filename } = await api.qrCheckin.getCodeQr({
          workplaceId: this.workplaceId,
          codeId: this.code.id,
        });

        if (!data?.size) {
          return;
        }

        const blob = data.type === 'image/svg+xml'
          ? data
          : new Blob([data], { type: 'image/svg+xml' });

        this.imageBlob = blob;
        this.imageUrl = URL.createObjectURL(blob);
        this.filename = filename;
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.image = false;
      }
    },

    downloadSvg() {
      if (!this.imageBlob) {
        return;
      }

      const filename = (this.filename || '')
        .replace(/["']/g, '')
        .trim() || this.fallbackFilename();

      downloadFile(this.imageBlob, filename);
    },

    revokeImage() {
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl);
      }

      this.imageBlob = null;
      this.imageUrl = null;
      this.filename = null;
    },

    onClosed() {
      this.revokeImage();

      if (document.querySelector('.modal.show')) {
        document.body.classList.add('modal-open');
      }
    },
  },

  beforeDestroy() {
    this.revokeImage();
  },
};
</script>

<style lang="scss">
.location-qr-code-preview-modal {
  .modal-content {
    width: 280px;
    margin: 0 auto;
  }

  &__body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__invalid {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 216px;
    height: 216px;
  }

  &__image {
    max-width: 216px;
    max-height: 216px;
    object-fit: contain;
  }

  &__name {
    color: #808080;
  }
}
</style>
